import { groupBy } from "lodash-es";
import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import IFieldModel from "@mapmycustomers/shared/types/fieldModel/IFieldModel";
import Organization from "@mapmycustomers/shared/types/Organization";
import SchemaField from "@mapmycustomers/shared/types/schema/SchemaField";

import { callApi } from "@app/store/api/callApi";
import { handleError } from "@app/store/errors/actions";
import { getOrganization, getOrganizationId } from "@app/store/iam";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";

import { fetchAllSchemas, fetchSchemaForEntityType, updateSchemaField } from "./actions";

export function* onFetchSchema({
  payload: entityType,
}: ReturnType<typeof fetchSchemaForEntityType.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const schema: SchemaField[] = yield callApi("fetchSchema", orgId, entityType);

    const fieldModel: IFieldModel = yield call(getFieldModelByEntityType, entityType);
    fieldModel.setSchema(schema);

    yield put(fetchSchemaForEntityType.success({ entityType, schema }));
  } catch (error) {
    yield put(fetchSchemaForEntityType.failure(error));
  }
}

export function* onFetchAllSchemas() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: SchemaField[] = yield callApi("fetchSchema", orgId);

    const schemasByType = groupBy(response, "entity") as Record<
      SchemaField["entity"],
      SchemaField[]
    >;

    yield put(fetchAllSchemas.success(schemasByType));
  } catch {
    yield put(fetchAllSchemas.failure());
    // error is handled in the onInitializeApp saga
  }
}

export function* onUpdateSchemaField({
  payload: { entityType, schema },
}: ReturnType<typeof updateSchemaField.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    yield callApi("updateSchema", org.id, entityType, schema);
    yield put(fetchSchemaForEntityType.request(entityType));
    yield put(updateSchemaField.success());
  } catch (error) {
    yield put(updateSchemaField.failure());
    yield put(handleError({ error }));
  }
}

export function* schemaSaga() {
  yield takeLatest(fetchAllSchemas.request, onFetchAllSchemas);
  yield takeEvery(fetchSchemaForEntityType.request, onFetchSchema);
  yield takeEvery(updateSchemaField.request, onUpdateSchemaField);
}
